import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import TableAdminLeaderBoard from "../../components/Tables/TableAdminLeaderBoard";
import InputSearch from "../../components/inputs/InputSearch";
import Pagination from "../../components/Tables/Pagination";
import {
  useInviteUserMutation,
  useInviteByCSVMutation,
} from "../../services/organization/adminUsers";
import { useGetAdminLeaderboardQuery } from "../../services/organization/leaderboard";
import InviteEmployeeModal from "../../components/Modals/InviteEmployeeModal";
import LoadingIndicator from "../../components/LoadingIndicator";
import EmptyState from "../../components/EmptyState";
import Icon from "../../components/icons/Icon";
import { UsersStrokeIcon } from "../../components/icons/Icons";
import { useTranslation } from "react-i18next";
import { useEmployeesStickyTableHeader } from "../../hooks/useEmployeesStickyTableHeader";

const AdminLeaderBoard = () => {
  const toolbarRef = useRef(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [usersFile, setUsersFile] = useState([]);

  const { t, i18n } = useTranslation(["common", "leaderboard", "employees"]);

  const validationSchema = yup.object({
    email: yup.string().email(t("validations.emailWrong")),
    type: yup.string().required(t("validations.emailRequired")),
  });

  const { data: usersList, isLoading: isGetLeaderboardLoading } =
    useGetAdminLeaderboardQuery({
      keyword: searchKeyword,
      page,
    });

  useEmployeesStickyTableHeader(toolbarRef, [usersList, searchKeyword, i18n.language]);

  const [inviteUser] = useInviteUserMutation();
  const [inviteByCSV] = useInviteByCSVMutation();

  const formik = useFormik({
    initialValues: {
      email: "",
      type: "email",
    },
    validationSchema: validationSchema,

    onSubmit: async (values) => {
      let result;
      if (values?.type == "email") result = await inviteUser(values?.email);
      else if (values?.type == "csv") {
        if (usersFile.length > 0) {
          let dataForm = new FormData();
          dataForm.append("users", usersFile[0]);
          result = await inviteByCSV(dataForm);
        }
      }

      if (result?.error) {
        toast.error(result?.error?.data?.message);
      } else {
        toast.success(t("invitationSuccess"));
      }
    },
  });

  if (isGetLeaderboardLoading && !usersList) return <LoadingIndicator />;

  return (
    <div className="employee ranks employees-page leaderboard-page">
      <InviteEmployeeModal />

      <header ref={toolbarRef} className="employees-page__toolbar">
        <div className="employees-page__intro">
          <h1 className="title-page">{t("leaderboard:leaderboard")}</h1>
          <ul className="employees-page__stats">
            <li>
              <span className="label label-black">
                <span className="me-2">{usersList?.pagination?.count}</span>
                {t("employee")}
              </span>
            </li>
          </ul>
        </div>

        <div className="employees-page__actions">
          <InputSearch
            placeHolder={t("leaderboard:searchEmployeesPlaceholder")}
            value={searchKeyword}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
              setPage(1);
            }}
          />
          <button
            type="button"
            className="btn btn-main-color font-15"
            data-bs-toggle="modal"
            data-bs-target="#staticBackdrop"
          >
            <span className="fas fa-user-plus fa-fw me-2"></span>
            {t("inviteNewEmployee")}
          </button>
        </div>
      </header>

      <div className="employees-page__body">
        {usersList?.data?.length ? (
          <>
            <div className="employees-page__table-wrap table-white">
              <TableAdminLeaderBoard usersList={usersList} />
            </div>
            <Pagination
              current={usersList?.pagination?.current}
              pages={usersList?.pagination?.pages}
              handlePagination={(page) => setPage(page)}
            />
          </>
        ) : (
          <div className="leaderboard-page__empty">
            <EmptyState
              iconComponent={
                <Icon
                  icon={<UsersStrokeIcon />}
                  iconName="users"
                  iconSize={38}
                  viewBox="0 0 25 25"
                  iconColor="#bbbabf"
                  hasStroke
                />
              }
              title={
                searchKeyword.trim()
                  ? t("leaderboard:noSearchResults")
                  : t("leaderboard:emptyLeaderboard")
              }
              emptyStateText={
                searchKeyword.trim()
                  ? t("leaderboard:noSearchResultsHint")
                  : t("employees:addEmployeeToAppear")
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeaderBoard;
